/**
 * Routine Finder — Shopify Helpers
 * Exposes: window.rfShopify
 *   .enrichProducts(products) — fetches live Shopify data and mutates each product in-place
 *   .addToCart(variantId, qty) → Promise
 *   .formatPrice(amount, code)  → formatted string
 */
(function () {
  'use strict';

  // Internal cache for deduplication — avoids fetching the same handle twice
  const _cache = {};

  // Map of Routine Finder product IDs → Shopify variant ID + product handle
  // Derived from master registry (products-registry-routinefinder.js)
  const _reg = window.rfProducts || {};
  const PRODUCT_MAP = {};
  for (const [id, p] of Object.entries(_reg)) {
    if (p.handle && p.variantId) {
      PRODUCT_MAP[id] = { variantId: p.variantId, handle: p.handle };
    }
  }

  async function _fetchProduct(handle) {
    if (_cache[handle]) return _cache[handle];
    const res = await fetch(`/products/${handle}.json`);
    if (!res.ok) throw new Error(`${handle} not found`);
    const { product } = await res.json();
    _cache[handle] = product;
    return product;
  }

  /**
   * Batch-fetch all unique handles in parallel (deduped).
   * Replaces the previous N+1 pattern — all requests fire simultaneously.
   */
  async function _prefetchProducts(handles) {
    const unique = [...new Set(handles)].filter(h => h && !_cache[h]);
    if (!unique.length) return;
    await Promise.allSettled(unique.map(h => _fetchProduct(h)));
  }

  /**
   * Mutates each product object in the array with live Shopify data.
   * Fields added: shopifyVariantId, shopifyProductId, productUrl, imageUrl,
   *               price, originalPrice, available, currencyCode.
   * Uses a single parallel prefetch pass to avoid N+1 sequential requests.
   */
  async function enrichProducts(products) {
    // 1. Prefetch all unique product handles in parallel (single batch)
    const handles = products.map(p => PRODUCT_MAP[p.id]?.handle).filter(Boolean);
    await _prefetchProducts(handles);

    // 2. Map cached data onto each product (all reads from cache — instant)
    products.forEach(p => {
      const map = PRODUCT_MAP[p.id];
      if (!map) return;
      p.shopifyVariantId = map.variantId;
      p.productUrl       = `/products/${map.handle}`;
      try {
        const sp = _cache[map.handle];
        if (!sp) return;
        const variant = sp.variants.find(v => String(v.id) === map.variantId);
        if (!variant) return;
        p.shopifyProductId = String(sp.id);
        p.imageUrl         = sp.images?.[0]?.src || '';
        p.price            = parseFloat(variant.price) || p.price;
        p.originalPrice    = variant.compare_at_price ? parseFloat(variant.compare_at_price) : null;
        p.available        = variant.available !== false;
        p.currencyCode     = (typeof Shopify !== 'undefined' && Shopify.currency?.active) || 'GBP';
      } catch (_) { /* keep static fallback price */ }
    });
  }

  /**
   * Collect section IDs the FoxTheme cart drawer + cart page need to re-render.
   * Mirrors the pattern in products-bundle.js so the drawer updates instantly.
   */
  function _getSectionsToRender() {
    let sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:grouped-sections', {
        bubbles: true,
        detail: { sections },
      })
    );
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
      sections = [...sections, ...cartDrawer.getSectionsToRender().map(s => s.id)];
    }
    const mainCart = document.querySelector('cart-items');
    if (mainCart && typeof mainCart.getSectionsToRender === 'function') {
      sections = [...sections, ...mainCart.getSectionsToRender().map(s => s.section)];
    }
    return sections;
  }

  /**
   * Add item(s) to cart using FoxTheme's cart_add_url with section rendering,
   * then emit PUB_SUB_EVENTS.cartUpdate so the cart drawer updates in real-time.
   * @param {Array<{id: string|number, quantity?: number}>} items
   */
  async function addToCartItems(items) {
    const cartAddUrl = window.FoxThemeSettings?.routes?.cart_add_url || '/cart/add.js';
    const sections   = _getSectionsToRender();

    const res = await fetch(cartAddUrl, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
      body:    JSON.stringify({
        items,
        sections,
        sections_url: window.location.pathname,
      }),
    });
    if (!res.ok) throw new Error('Cart add failed');
    const data = await res.json();
    if (data?.status) throw new Error(data.description || 'Cart add failed');

    // Emit cartUpdate so cart drawer, free-shipping bar, etc. update instantly
    if (window.FoxThemeEvents && typeof PUB_SUB_EVENTS !== 'undefined') {
      window.FoxThemeEvents.emit(PUB_SUB_EVENTS.cartUpdate, data);
    }
    return data;
  }

  /** POST a single line item to cart (convenience wrapper) */
  async function addToCart(variantId, quantity = 1) {
    return addToCartItems([{ id: variantId, quantity }]);
  }

  /**
   * Set order-level cart attributes for Routine Finder attribution.
   * These appear in Shopify admin under "Additional details" on the order.
   * Non-blocking — silently ignores failures.
   */
  async function setCartAttributes(answers, heroProductId, sessionId) {
    try {
      await fetch('/cart/update.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({
          attributes: {
            'Routine Finder Used':  'true',
            'RF Primary Concern':   answers.primary_concern   || '',
            'RF Secondary Concern': answers.secondary_concern || '',
            'RF Hero Product':      heroProductId              || '',
            'RF Session ID':        sessionId                  || '',
          },
        }),
      });
    } catch (_) { /* non-critical */ }
  }

  /** Format a price with currency symbol. Falls back gracefully. */
  function formatPrice(amount, currencyCode) {
    const code = currencyCode
      || (typeof Shopify !== 'undefined' && Shopify.currency?.active)
      || 'GBP';
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency', currency: code, currencyDisplay: 'narrowSymbol',
      }).format(amount);
    } catch (_) {
      return `${code} ${Number(amount).toFixed(2)}`;
    }
  }

  /** Reverse-map a Shopify product handle → Routine Finder product ID. */
  function getProductIdByHandle(handle) {
    for (const [id, entry] of Object.entries(PRODUCT_MAP)) {
      if (entry.handle === handle) return id;
    }
    return null;
  }

  window.rfShopify = { enrichProducts, addToCart, addToCartItems, setCartAttributes, formatPrice, getProductIdByHandle, PRODUCT_MAP };
})();
